export type ThemeName = "mahogany" | "persian" | "deco" | "monte";

/**
 * The cross-game palette shared by every BN-games app. Each game extends this
 * with its own render-specific fields (e.g. point-triangle gradients for
 * backgammon, dark/light-square gradients for checkers, dice/king-crown
 * colors) and supplies a game-specific monogram letter.
 */
export interface BaseThemePalette {
  readonly name: ThemeName;
  readonly label: string;
  readonly tagline: string;

  readonly pageBackground: string;
  readonly pageGrad: string;
  readonly menuBackdrop: string;

  readonly leather: string;
  readonly leatherDeep: string;

  readonly woodBase: string;
  readonly woodDark: string;
  readonly woodLight: string;
  readonly woodGrainA: string;
  readonly woodGrainB: string;

  readonly feltBase: string;
  readonly feltDark: string;

  readonly brass: string;
  readonly brassDeep: string;
  readonly brassHi: string;
  readonly accent: string;

  readonly ink: string;
  readonly inkSoft: string;
  readonly inkDim: string;
  readonly rule: string;

  readonly btnBg: string;
  readonly btnBgHover: string;
  readonly btnBorder: string;

  readonly boardWoodBase: string;
  readonly boardWoodDark: string;
  readonly boardWoodLight: string;

  readonly borderWoodBase: string;
  readonly borderWoodDark: string;
  readonly borderWoodLight: string;

  readonly whiteCheckerColor: string;
  readonly whiteCheckerHighlight: string;
  readonly whiteCheckerShadow: string;
  readonly whiteCheckerBorder: string;

  readonly blackCheckerColor: string;
  readonly blackCheckerHighlight: string;
  readonly blackCheckerShadow: string;
  readonly blackCheckerBorder: string;

  readonly highlightColor: string;
  readonly validDestColor: string;
  readonly hoverGlowColor: string;

  readonly bannerBg: string;
  readonly bannerBorder: string;
  readonly bannerTextColor: string;
  readonly bannerWinBg: string;
}

export const BASE_MAHOGANY: BaseThemePalette = {
  name: "mahogany",
  label: "Mahogany Club",
  tagline: "An evening at the gentlemen's club",

  pageBackground: "#1a0f08",
  pageGrad: "radial-gradient(120% 80% at 50% 0%, #2a160c 0%, #150a04 70%)",
  menuBackdrop: "radial-gradient(120% 80% at 50% 0%, #2a160c 0%, #150a04 70%)",

  leather: "#3b1f12",
  leatherDeep: "#23120a",

  woodBase: "#7a3a1a",
  woodDark: "#3d1808",
  woodLight: "#a8542a",
  woodGrainA: "rgba(34,12,4,0.55)",
  woodGrainB: "rgba(220,150,90,0.10)",

  feltBase: "#5a2412",
  feltDark: "#3a1408",

  brass: "#caa05a",
  brassDeep: "#8a6628",
  brassHi: "#f4d894",
  accent: "#caa05a",

  ink: "#f0e2c2",
  inkSoft: "#c9b58a",
  inkDim: "#a89472",
  rule: "rgba(202,160,90,0.35)",

  btnBg: "#2c160b",
  btnBgHover: "#3e1f10",
  btnBorder: "rgba(202,160,90,0.55)",

  boardWoodBase: "#7a3a1a",
  boardWoodDark: "#3d1808",
  boardWoodLight: "#a8542a",

  borderWoodBase: "#3b1f12",
  borderWoodDark: "#23120a",
  borderWoodLight: "#4a2818",

  whiteCheckerColor: "#f3e5c2",
  whiteCheckerHighlight: "#fff8e0",
  whiteCheckerShadow: "#c8b88a",
  whiteCheckerBorder: "#a78858",

  blackCheckerColor: "#1a0a06",
  blackCheckerHighlight: "#3a1c10",
  blackCheckerShadow: "#08040a",
  blackCheckerBorder: "#5a2a14",

  highlightColor: "rgba(244, 216, 148, 0.4)",
  validDestColor: "rgba(202, 160, 90, 0.32)",
  hoverGlowColor: "rgba(244, 216, 148, 0.8)",

  bannerBg: "rgba(20, 10, 4, 0.6)",
  bannerBorder: "rgba(202, 160, 90, 0.2)",
  bannerTextColor: "#f0e2c2",
  bannerWinBg: "rgba(40, 20, 8, 0.75)",
};

export const BASE_PERSIAN: BaseThemePalette = {
  name: "persian",
  label: "Persian Bazaar",
  tagline: "Inlaid walnut and lapis from Isfahan",

  pageBackground: "#0d1a26",
  pageGrad: "radial-gradient(120% 80% at 50% 0%, #15273a 0%, #08121c 70%)",
  menuBackdrop: "radial-gradient(120% 80% at 50% 0%, #15273a 0%, #08121c 70%)",

  leather: "#1a2f44",
  leatherDeep: "#0b1825",

  woodBase: "#5a3a20",
  woodDark: "#2a1808",
  woodLight: "#8a5a2c",
  woodGrainA: "rgba(15,8,2,0.5)",
  woodGrainB: "rgba(220,180,120,0.10)",

  feltBase: "#0f3a4a",
  feltDark: "#06202c",

  brass: "#d4a55a",
  brassDeep: "#8a6028",
  brassHi: "#f0d28a",
  accent: "#3aa6b0",

  ink: "#ece2c0",
  inkSoft: "#c4b58a",
  inkDim: "#92a4b2",
  rule: "rgba(212,165,90,0.35)",

  btnBg: "#0e2330",
  btnBgHover: "#173040",
  btnBorder: "rgba(212,165,90,0.5)",

  boardWoodBase: "#5a3a20",
  boardWoodDark: "#2a1808",
  boardWoodLight: "#8a5a2c",

  borderWoodBase: "#1a2f44",
  borderWoodDark: "#0b1825",
  borderWoodLight: "#243f56",

  whiteCheckerColor: "#ece2c0",
  whiteCheckerHighlight: "#fff7d8",
  whiteCheckerShadow: "#bca888",
  whiteCheckerBorder: "#a08858",

  blackCheckerColor: "#0a1820",
  blackCheckerHighlight: "#1a3848",
  blackCheckerShadow: "#040a10",
  blackCheckerBorder: "#2a4858",

  highlightColor: "rgba(58, 166, 176, 0.35)",
  validDestColor: "rgba(212, 165, 90, 0.32)",
  hoverGlowColor: "rgba(240, 210, 138, 0.8)",

  bannerBg: "rgba(8, 18, 28, 0.65)",
  bannerBorder: "rgba(212, 165, 90, 0.2)",
  bannerTextColor: "#ece2c0",
  bannerWinBg: "rgba(15, 40, 60, 0.8)",
};

export const BASE_DECO: BaseThemePalette = {
  name: "deco",
  label: "Art Deco Noir",
  tagline: "Manhattan, 1928",

  pageBackground: "#0a0a0a",
  pageGrad: "radial-gradient(120% 80% at 50% 0%, #181613 0%, #050505 70%)",
  menuBackdrop: "radial-gradient(120% 80% at 50% 0%, #181613 0%, #050505 70%)",

  leather: "#161412",
  leatherDeep: "#0a0908",

  woodBase: "#1a1816",
  woodDark: "#0a0908",
  woodLight: "#2a2724",
  woodGrainA: "rgba(0,0,0,0.6)",
  woodGrainB: "rgba(212,176,108,0.06)",

  feltBase: "#141210",
  feltDark: "#0a0908",

  brass: "#d4b06c",
  brassDeep: "#8a7036",
  brassHi: "#f0d898",
  accent: "#d4b06c",

  ink: "#f2ecdc",
  inkSoft: "#c8bda0",
  inkDim: "#8c8272",
  rule: "rgba(212,176,108,0.4)",

  btnBg: "#0e0d0c",
  btnBgHover: "#1a1815",
  btnBorder: "rgba(212,176,108,0.55)",

  boardWoodBase: "#1a1816",
  boardWoodDark: "#0a0908",
  boardWoodLight: "#2a2724",

  borderWoodBase: "#161412",
  borderWoodDark: "#0a0908",
  borderWoodLight: "#22201d",

  whiteCheckerColor: "#f2ecdc",
  whiteCheckerHighlight: "#fff8e8",
  whiteCheckerShadow: "#bcb088",
  whiteCheckerBorder: "#9c8c64",

  blackCheckerColor: "#0a0908",
  blackCheckerHighlight: "#1c1a18",
  blackCheckerShadow: "#040404",
  blackCheckerBorder: "#3a3530",

  highlightColor: "rgba(240, 216, 152, 0.4)",
  validDestColor: "rgba(212, 176, 108, 0.32)",
  hoverGlowColor: "rgba(240, 216, 152, 0.85)",

  bannerBg: "rgba(0, 0, 0, 0.7)",
  bannerBorder: "rgba(212, 176, 108, 0.25)",
  bannerTextColor: "#f2ecdc",
  bannerWinBg: "rgba(20, 18, 14, 0.85)",
};

export const BASE_MONTE: BaseThemePalette = {
  name: "monte",
  label: "Monte Carlo",
  tagline: "High stakes on the Riviera",

  pageBackground: "#0a1a12",
  pageGrad: "radial-gradient(120% 80% at 50% 0%, #122a1d 0%, #061008 70%)",
  menuBackdrop: "radial-gradient(120% 80% at 50% 0%, #122a1d 0%, #061008 70%)",

  leather: "#0f2418",
  leatherDeep: "#06150c",

  woodBase: "#3a2410",
  woodDark: "#1a0e04",
  woodLight: "#5a3a1c",
  woodGrainA: "rgba(0,0,0,0.5)",
  woodGrainB: "rgba(200,160,90,0.08)",

  feltBase: "#0f3a26",
  feltDark: "#062418",

  brass: "#d4b06c",
  brassDeep: "#8a7036",
  brassHi: "#f0d898",
  accent: "#db5440",

  ink: "#f2ecd8",
  inkSoft: "#c8bda0",
  inkDim: "#92a092",
  rule: "rgba(212,176,108,0.4)",

  btnBg: "#0c1d14",
  btnBgHover: "#15301f",
  btnBorder: "rgba(212,176,108,0.5)",

  boardWoodBase: "#3a2410",
  boardWoodDark: "#1a0e04",
  boardWoodLight: "#5a3a1c",

  borderWoodBase: "#0f2418",
  borderWoodDark: "#06150c",
  borderWoodLight: "#173628",

  whiteCheckerColor: "#f2ecd8",
  whiteCheckerHighlight: "#fff8e0",
  whiteCheckerShadow: "#bcb088",
  whiteCheckerBorder: "#9c8c5c",

  blackCheckerColor: "#0a0a0a",
  blackCheckerHighlight: "#1a1a1a",
  blackCheckerShadow: "#040404",
  blackCheckerBorder: "#2a2a2a",

  highlightColor: "rgba(192, 57, 43, 0.4)",
  validDestColor: "rgba(212, 176, 108, 0.32)",
  hoverGlowColor: "rgba(240, 216, 152, 0.85)",

  bannerBg: "rgba(6, 20, 12, 0.7)",
  bannerBorder: "rgba(212, 176, 108, 0.22)",
  bannerTextColor: "#f2ecd8",
  bannerWinBg: "rgba(15, 40, 28, 0.8)",
};

export const BASE_PALETTES: Record<ThemeName, BaseThemePalette> = {
  mahogany: BASE_MAHOGANY,
  persian: BASE_PERSIAN,
  deco: BASE_DECO,
  monte: BASE_MONTE,
};
